import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GrMicrophone } from 'react-icons/gr';
import { FiVideo, FiBookOpen } from 'react-icons/fi';
import { FaRegPlayCircle } from 'react-icons/fa';

import { ROUTES } from 'utils/constants';
import { useEvieAppContext } from '../EvieAppProvider';
import { useEvieHomeContext } from '../EvieHomeProvider';
import { TASK_TYPES, HOME_STATUS } from '../utils';

import LockedIcon from '../LockedIcon';
import CompletedIndicator from '../CompletedIndicator';

const ContentCard = ({ task, isProgram }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { completedActivities, isPremium } = useEvieAppContext();
  const { selectTask, changeHomeStatus } = useEvieHomeContext();

  const {
    __typename: type,
    title: contentTitle,
    id: taskId,
    thumbnailImage: { thumb: capsuleReadingBgImage } = {},
    image: { thumb: audioBgImage } = {},
  } = task;

  const isCardCompleted = () => completedActivities.some((activity) => activity.id === taskId);

  const isCompleted = isCardCompleted();

  const icon = {
    [TASK_TYPES.CAPSULE]: <FiVideo />,
    [TASK_TYPES.READING]: <FiBookOpen />,
    [TASK_TYPES.AUDIO]: <GrMicrophone />,
  };

  const bgImage = {
    [TASK_TYPES.CAPSULE]: capsuleReadingBgImage,
    [TASK_TYPES.READING]: capsuleReadingBgImage,
    [TASK_TYPES.AUDIO]: audioBgImage,
  };

  const title = {
    [TASK_TYPES.CAPSULE]: t('evieApp.contentCard.capsuleLabel'),
    [TASK_TYPES.READING]: t('evieApp.contentCard.readingLabel'),
    [TASK_TYPES.AUDIO]: t('evieApp.contentCard.audioLabel'),
  };

  const isBlocked = !isPremium && isProgram;

  const handleSelectTask = () => {
    // The home status outlives the route, so setting it without a task left
    // Home rendering an empty TaskController.
    if (isBlocked) {
      push(`${ROUTES.EVIE_ONBOARDING}?view=plans`);
      return;
    }
    selectTask(task);
    changeHomeStatus(HOME_STATUS.TASK);
  };

  return (
    <button
      type="button"
      className={`content-card ${isBlocked ? 'content-card--blocked' : ''} ${isCompleted ? '' : 'not-completed-task'}`}
      onClick={handleSelectTask}
    >
      {!isBlocked && isCompleted && <CompletedIndicator />}
      {isBlocked ? (
        <div className="content-card__locked">
          <LockedIcon />
        </div>
      ) : null}
      {type === TASK_TYPES.CAPSULE && (
        <FaRegPlayCircle
          className={`content-card__playicon ${isCompleted ? 'content-card__playicon--completed' : ''}`}
        />
      )}
      <img src={bgImage[type]} alt="card" className="content-card__img" />
      <div className="content-card__footer">
        <div className="content-card__footertitle">
          <div className="content-card__footericon">{icon[type]}</div>
          <h3 className="content-card__footertitle_label">{title[type]}</h3>
        </div>
        <p className="content-card__footer_description">{contentTitle}</p>
      </div>
    </button>
  );
};

ContentCard.defaultProps = {
  isProgram: false,
};

ContentCard.propTypes = {
  task: PropTypes.shape({
    __typename: PropTypes.oneOf(['Reading', 'Audio', 'ShortVideo']),
    title: PropTypes.string,
    id: PropTypes.string,
    thumbnailImage: PropTypes.shape({ thumb: PropTypes.string }),
    image: PropTypes.shape({ thumb: PropTypes.string }),
  }).isRequired,
  isProgram: PropTypes.bool,
};

export default ContentCard;
